/**
 * Hoje (spec 018, US1): o que precisa de mim AGORA, e por quê.
 *
 * Quatro indicadores clicáveis e uma fila de prioridades — os dois de UM
 * endpoint e do mesmo escopo (FR-001): o número do indicador é o total da
 * lista que ele abre, por construção. Cada item diz o motivo e traz a ação:
 * Responder (abre com o compositor em foco no canal certo), Assumir ou Abrir.
 * "Minha visão / Equipe" muda a página inteira, e tudo fica na URL.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  AlarmClock,
  CheckCircle2,
  Clock3,
  Hourglass,
  MessageCircle,
  MessageSquareReply,
  Ticket,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import {
  WORK_ACTION_LABELS,
  capitalize,
  situationLabel,
  type WorkFocus,
  type WorkItem,
  type WorkScope,
  type WorkToday,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useRealtimeConnected } from '@/lib/realtime';
import { useTerms } from '@/shared/tenant-profile';
import { CHAT_ORIGIN_LABELS } from '@erp/shared';
import type { Visao } from '@/features/atendimentos/atendimentos-search';
import { MyWeekAgendaSection } from '@/features/agenda/my-week-agenda';
import { workTodayQuery } from './work-queries';
import { useAssumeWork } from './work-actions';
import { TONE_TEXT, reasonText, reasonTone } from './work-format';

export const hojeSearchSchema = z.object({
  escopo: z.enum(['meu', 'equipe']).default('meu').catch('meu'),
  foco: z.enum(['precisa_resposta', 'prazo_em_risco', 'sem_responsavel', 'aguardando_cliente']).optional().catch(undefined),
});
export type HojeSearch = z.infer<typeof hojeSearchSchema>;

const FOCUS_OF = {
  precisa_resposta: 'needs_reply',
  prazo_em_risco: 'at_risk',
  sem_responsavel: 'unassigned',
  aguardando_cliente: 'waiting_client',
} as const satisfies Record<NonNullable<HojeSearch['foco']>, WorkFocus>;

/** o indicador abre a visão de MESMO nome na lista de Atendimentos */
const VIEW_OF: Record<WorkFocus, Visao> = {
  needs_reply: 'precisa_resposta',
  at_risk: 'prazo_em_risco',
  unassigned: 'sem_responsavel',
  waiting_client: 'aguardando_cliente',
};

interface IndicatorDef {
  foco: NonNullable<HojeSearch['foco']>;
  focus: WorkFocus;
  icon: LucideIcon;
  tone: string;
  value: (t: WorkToday) => number;
}

const INDICATORS: IndicatorDef[] = [
  { foco: 'precisa_resposta', focus: 'needs_reply', icon: MessageSquareReply, tone: 'text-[#D48806]', value: (t) => t.counters.needsReply },
  { foco: 'prazo_em_risco', focus: 'at_risk', icon: AlarmClock, tone: 'text-[#CF1322]', value: (t) => t.counters.atRisk },
  { foco: 'sem_responsavel', focus: 'unassigned', icon: UserX, tone: 'text-[#1677FF]', value: (t) => t.counters.unassigned },
  { foco: 'aguardando_cliente', focus: 'waiting_client', icon: Hourglass, tone: 'text-muted-foreground', value: (t) => t.counters.waitingClient },
];

/** relógio da página — os "há X" andam sem refazer a consulta */
function useNow(everyMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

export function HojePage() {
  const search = hojeSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  const terms = useTerms();
  const now = useNow();
  const scope: WorkScope = search.escopo === 'equipe' ? 'team' : 'mine';
  const focus = search.foco ? FOCUS_OF[search.foco] : undefined;
  const realtimeUp = useRealtimeConnected();
  const todayQ = useQuery({
    ...workTodayQuery(scope, focus),
    // o realtime invalida a família; o polling é o fallback de quando o SSE cai
    refetchInterval: realtimeUp ? 60_000 : 20_000,
  });
  // os indicadores são do escopo, não do foco: vêm da consulta sem foco quando
  // há um — a mesma chave da página sem foco, então vem do cache
  const baseQ = useQuery({ ...workTodayQuery(scope, undefined), enabled: focus !== undefined });
  const counters = focus ? baseQ.data : todayQ.data;
  const { assume, pending: assuming, error: assumeError, dialog } = useAssumeWork();

  const setSearch = (patch: Partial<HojeSearch>) =>
    void navigate({ to: '/s/hoje', search: { ...search, ...patch }, replace: true });

  const open = (item: WorkItem, reply: boolean) => {
    if (item.kind === 'ticket') {
      void navigate({
        to: '/s/atendimentos/$ticketNumber',
        params: { ticketNumber: String(item.ticketNumber) },
        search: reply ? { responder: 1 } : {},
      });
    } else {
      void navigate({ to: '/s/conversas', search: { chat: item.chatId } });
    }
  };

  const act = (item: WorkItem) => {
    if (item.action === 'open') return open(item, false);
    if (item.action === 'reply') return open(item, true);
    assume(
      item.kind === 'ticket' ? { kind: 'ticket', ticketNumber: item.ticketNumber } : { kind: 'chat', chatId: item.chatId },
      () => open(item, true)
    );
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Hoje</h1>
          <p className="text-[13px] text-muted-foreground">
            {now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5" role="group" aria-label="Escopo">
          {(['meu', 'equipe'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={search.escopo === value}
              onClick={() => setSearch({ escopo: value })}
              className={cn(
                'h-8 rounded-[5px] px-3 text-[13px] font-medium transition-colors',
                search.escopo === value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {value === 'meu' ? 'Minha visão' : 'Equipe'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {INDICATORS.map((ind) => {
          const active = search.foco === ind.foco;
          const value = counters ? ind.value(counters) : undefined;
          const label =
            ind.focus === 'needs_reply'
              ? 'Precisa de resposta'
              : ind.focus === 'at_risk'
                ? 'Prazo em risco'
                : ind.focus === 'unassigned'
                  ? 'Sem responsável'
                  : `Aguardando ${terms.client}`;
          const detail =
            counters && ind.focus === 'needs_reply' && counters.needsReplyBreakdown.chats > 0
              ? `${counters.needsReplyBreakdown.tickets} ${counters.needsReplyBreakdown.tickets === 1 ? terms.ticket : terms.tickets} · ${counters.needsReplyBreakdown.chats} ${counters.needsReplyBreakdown.chats === 1 ? 'conversa' : 'conversas'}`
              : counters && ind.focus === 'waiting_client'
                ? `${counters.waitingClientBreakdown.waiting_client} ${situationLabel(terms, 'waiting_client').toLowerCase()} · ${counters.waitingClientBreakdown.waiting_contact} ${situationLabel(terms, 'waiting_contact').toLowerCase()}`
                : null;
          return (
            <button
              key={ind.foco}
              type="button"
              aria-pressed={active}
              onClick={() => setSearch({ foco: active ? undefined : ind.foco })}
              className={cn(
                'flex flex-col gap-1 rounded-lg border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/60',
                active && 'border-primary ring-2 ring-primary/20'
              )}
            >
              <span className="flex items-center gap-2 text-[13px] text-muted-foreground">
                <ind.icon className={cn('size-4', ind.tone)} />
                {label}
              </span>
              {value === undefined ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <span className={cn('text-2xl font-semibold tabular-nums', value > 0 ? ind.tone : 'text-muted-foreground/60')}>
                  {value}
                </span>
              )}
              {detail ? <span className="text-[11px] text-muted-foreground">{detail}</span> : null}
            </button>
          );
        })}
      </div>

      <section className="rounded-lg border bg-card shadow-sm" aria-label="Fila de prioridades">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            {focus ? INDICATOR_TITLE(focus, terms.client) : 'Fila de prioridades'}
          </h2>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {todayQ.isFetching ? <Spinner className="size-3.5" /> : null}
            {focus ? (
              <button type="button" className="font-medium text-primary hover:underline" onClick={() => setSearch({ foco: undefined })}>
                Voltar à fila
              </button>
            ) : null}
            <Link
              to="/s/atendimentos"
              search={{ visao: focus ? VIEW_OF[focus] : 'todos', ...(scope === 'mine' && focus ? { escopo: 'meu' } : {}) }}
              className="font-medium text-primary hover:underline"
            >
              Ver em {terms.tickets}
            </Link>
          </div>
        </header>

        {todayQ.isPending ? (
          <div className="space-y-2 p-4" aria-busy>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : todayQ.isError ? (
          <p className="p-6 text-center text-sm text-destructive">Não foi possível carregar o Hoje. Tente de novo.</p>
        ) : todayQ.data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
            <CheckCircle2 className="size-8 text-primary" strokeWidth={1.5} />
            <p className="text-sm font-medium">Nada precisa de você agora</p>
            <Link to="/s/atendimentos" search={{ visao: 'todos' }} className="text-sm font-medium text-primary hover:underline">
              Ver todos os {terms.tickets}
            </Link>
          </div>
        ) : (
          <ul className="divide-y">
            {todayQ.data.items.map((item) => (
              <WorkRow
                key={item.kind === 'ticket' ? `t${item.ticketNumber}` : `c${item.chatId}`}
                item={item}
                now={now}
                busy={assuming}
                onOpen={() => open(item, false)}
                onAct={() => act(item)}
              />
            ))}
          </ul>
        )}
        {todayQ.data?.truncated ? (
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            Mostrando {todayQ.data.items.length} de {todayQ.data.total} — o resto está em {terms.tickets}.
          </p>
        ) : null}
        {assumeError ? <p className="border-t px-4 py-2 text-xs text-destructive">{assumeError}</p> : null}
      </section>
      {/* spec 039: a agenda da semana também no início do Simplificado — só a minha */}
      {scope === 'mine' ? <MyWeekAgendaSection /> : null}
      {dialog}
    </div>
  );
}

function INDICATOR_TITLE(focus: WorkFocus, client: string): string {
  switch (focus) {
    case 'needs_reply':
      return 'Precisa de resposta';
    case 'at_risk':
      return 'Prazo em risco';
    case 'unassigned':
      return 'Sem responsável';
    case 'waiting_client':
      return `Aguardando ${client}`;
  }
}

function WorkRow({
  item,
  now,
  busy,
  onOpen,
  onAct,
}: {
  item: WorkItem;
  now: Date;
  busy: boolean;
  onOpen: () => void;
  onAct: () => void;
}) {
  const terms = useTerms();
  const tone = reasonTone(item.reason);
  const Icon = item.kind === 'ticket' ? Ticket : MessageCircle;
  const title =
    item.kind === 'ticket'
      ? `#${item.ticketNumber} · ${item.title}`
      : `Conversa com ${item.requestorName} (${CHAT_ORIGIN_LABELS[item.origin]})`;
  const context =
    item.kind === 'ticket'
      ? [item.client.name, item.resourceLabel, item.desk.name, item.responsible ? item.responsible.name : null]
      : [item.client?.name, item.desk?.name, item.ticketNumber ? `${capitalize(terms.ticket)} #${item.ticketNumber} encerrado` : `sem ${terms.ticket}`];
  return (
    <li className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-medium text-foreground">{title}</span>
        <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 text-xs">
          {/* o POR QUÊ vem antes de tudo: é o que decide a ordem da fila */}
          <span className={cn('flex shrink-0 items-center gap-1 font-medium', TONE_TEXT[tone])}>
            <Clock3 className="size-3.5" aria-hidden />
            {reasonText(item.reason, terms, now)}
          </span>
          <span className="min-w-0 truncate text-muted-foreground">{context.filter(Boolean).join(' · ')}</span>
        </span>
      </button>
      <Button
        size="sm"
        variant={item.action === 'open' ? 'outline' : 'default'}
        className="w-24 shrink-0"
        disabled={busy && item.action === 'assume'}
        onClick={onAct}
      >
        {WORK_ACTION_LABELS[item.action]}
      </Button>
    </li>
  );
}
